import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Input,
    Icon,
    Link,
    Heading,
    Image,
    createListCollection,
    Spinner,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useForm, useController } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CompanyLogoHeader } from '../../components/auth/CompanyLogoHeader';
import Button from '../../components/base/Button';
import Select from '../../components/base/BaseSelect';
import { RememberMeCheckbox } from '../../components/base/RememberMeCheckbox';
import { Tooltip } from '../../components/ui/tooltip';
import { useLoginMutation, useLazyGetCompaniesByEmailQuery, useGetCustomerQuery } from '../../api';
import { useTranslation } from '../../hooks/useTranslation';
import { createLoginSchema, LoginFormData, isLoginEmailValidForQuery } from '../../schemas/loginSchema';
import { modalManager } from '../../utils/modalManager';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [login, { isLoading }] = useLoginMutation();
    const { t, changeLanguage } = useTranslation({ setLangFromURL: true });

    // Проверяем, залогинен ли пользователь при открытии страницы
    const {
        data: customer,
        isError: isCustomerError,
        isLoading: isCheckingAuth,
    } = useGetCustomerQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });

    // Если пользователь уже залогинен, перенаправляем на страницу оплат
    useEffect(() => {
        if (customer && !isCustomerError) {
            navigate('/payment', { replace: true });
        }
    }, [customer, isCustomerError, navigate]);

    // Состояние для выбранной компании
    const [selectedCompany, setSelectedCompany] = useState<{
        company_name: string;
        login: string;
    } | null>(null);

    // Состояние для показа/скрытия пароля
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        getFieldState,
        formState: { errors, isValid },
        setError,
        control,
        watch,
        getValues,
    } = useForm<LoginFormData>({
        resolver: yupResolver(createLoginSchema(t)),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            email: '',
            password: '',
            rememberMe: false,
        },
    });

    // Получаем значение email из формы
    const emailValue = watch('email');
    const { invalid: isEmailInvalid } = getFieldState('email');

    // Проверяем, должно ли поле пароля быть дизейблено
    const isPasswordDisabled = !emailValue || isEmailInvalid;

    // Чтобы не делать повторные запросы на один и тот же email при автоподстановке.
    const lastRequestedEmailRef = useRef<string>('');
    const [
        fetchCompaniesByEmail,
        { data: companies, isFetching: isFetchingCompanies, reset: resetCompanies },
    ] = useLazyGetCompaniesByEmailQuery();

    useEffect(() => {
        if (!emailValue?.trim()) {
            resetCompanies();
            lastRequestedEmailRef.current = '';
            return;
        }
        const latest = getValues('email')?.trim() ?? '';
        if (!isLoginEmailValidForQuery(latest)) {
            resetCompanies();
            lastRequestedEmailRef.current = '';
            return;
        }

        if (latest === lastRequestedEmailRef.current) return;
        lastRequestedEmailRef.current = latest;
        fetchCompaniesByEmail(latest);
    }, [emailValue]);

    // Создаем коллекцию для Select
    const companiesCollection = useMemo(() => {
        return createListCollection({
            items:
                companies?.map((company) => ({
                    value: company.login,
                    label: company.company_name,
                })) || [],
        });
    }, [companies]);

    // Автоматически выбираем первую компанию при получении данных
    useEffect(() => {
        if (companies && companies.length > 0) {
            setSelectedCompany(companies[0]);
        } else {
            setSelectedCompany(null);
        }
    }, [companies]);

    const rememberMeController = useController({
        control,
        name: 'rememberMe',
        defaultValue: false,
    });

    // Функция обработки ошибок логина
    const handleLoginError = (err: any) => {
        switch (err?.status) {
            case 401:
                setError('root', {
                    type: 'manual',
                    message: t.login.errors.invalidCredentials,
                });
                break;
            case 429:
                setError('root', {
                    type: 'manual',
                    message: t.login.errors.tooManyAttempts,
                });
                break;
            default:
                setError('root', {
                    type: 'manual',
                    message: t.login.errors.authError,
                });
                break;
        }
    };

    const onSubmit = async (data: LoginFormData) => {
        try {
            const result = await login({
                login: selectedCompany?.login || '',
                password: data.password,
                save_auth: data.rememberMe,
            }).unwrap();

            if (result) {
                changeLanguage(result.language);
                navigate('/payment');
            }
        } catch (err: any) {
            handleLoginError(err);
        }
    };

    const handleForgotPasswordClick = (e: React.MouseEvent) => {
        e.preventDefault();
        const email = emailValue?.trim();
        if (!email) {
            modalManager.show({
                title: t.login.form.forgotPasswordEmailRequiredTitle,
                message: t.login.form.forgotPasswordEmailRequiredMessage,
                buttons: [
                    {
                        type: 'secondary',
                        text: t.common.ok,
                        onClick: () => modalManager.remove(),
                    },
                ],
                size: 'md',
            });
            return;
        }
        navigate('/recovery/request', {
            state: {
                email,
                companyName: selectedCompany?.company_name,
                login: selectedCompany?.login,
            },
        });
    };

    // Показываем загрузку пока проверяем авторизацию
    if (isCheckingAuth) {
        return (
            <Box
                flex={1}
                bg="gray.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
                minH="100vh"
            >
                <VStack gap={6} align="center">
                    {/* Логотип */}
                    <Box display="flex" alignItems="center" justifyContent="center" w={16} h={16}>
                        <Image src="/favicon-192x192.png" alt="Office Hof" boxSize={16} objectFit="contain" />
                    </Box>

                    {/* Заголовок */}
                    <Heading size="xl" color="gray.800" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        Office Hof
                    </Heading>

                    {/* Спиннер */}
                    <Spinner size="xl" color="yellow.600" />

                    {/* Текст */}
                    <Text fontSize="lg" color="gray.700" fontWeight="medium">
                        {t.login.loadingData}
                    </Text>
                </VStack>
            </Box>
        );
    }

    return (
        <Box
            minH="100vh"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={4}
            style={{
                touchAction: 'manipulation',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
            }}
        >
            <Box w="full" maxW="md" bg="white" p={8}>
                <VStack gap={8} align="stretch">
                    {/* Логотип и название */}
                    <CompanyLogoHeader />

                    {/* Заголовок */}
                    <VStack gap={2} align="center">
                        <Heading size="lg" color="gray.800" textAlign="center">
                            {t.login.title}
                        </Heading>
                    </VStack>

                    {/* Форма */}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <VStack gap={6} align="stretch">
                            {/* Общие ошибки формы */}
                            {errors.root && (
                                <Box
                                    p={4}
                                    bg="red.50"
                                    border="1px solid"
                                    borderColor="red.200"
                                    borderRadius="md"
                                >
                                    <Text color="red.600" fontSize="sm">
                                        {errors.root.message}
                                    </Text>
                                </Box>
                            )}
                            {/* Поле логина */}
                            <Box>
                                <HStack justify="space-between" align="center" mb={3}>
                                    <Text fontSize="md" fontWeight="bold" color="gray.800">
                                        {t.login.form.emailLabel}
                                    </Text>
                                    {isFetchingCompanies && <Spinner size="sm" color="yellow.600" />}
                                </HStack>
                                <Input
                                    type="email"
                                    inputMode="email"
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    placeholder={t.login.form.emailPlaceholder}
                                    {...register('email')}
                                    size="lg"
                                    h="48px"
                                    borderRadius="0"
                                    colorPalette={errors.email ? 'red' : 'gray'}
                                />
                                {errors.email && (
                                    <Text color="red.500" fontSize="sm" mt={1}>
                                        {errors.email.message}
                                    </Text>
                                )}
                            </Box>

                            {/* Выбор компании */}
                            {companies && companies.length > 1 && (
                                <Box>
                                    <Text fontSize="md" fontWeight="bold" color="gray.800" mb={3}>
                                        {t.login.form.selectCompany}
                                    </Text>
                                    <Select
                                        collection={companiesCollection}
                                        value={selectedCompany ? [selectedCompany.login] : []}
                                        onValueChange={(selectedLogin: string) => {
                                            const company = companies.find((c) => c.login === selectedLogin);
                                            setSelectedCompany(company || null);
                                        }}
                                        placeholder={t.login.form.selectCompany}
                                        variant="white"
                                    />
                                </Box>
                            )}

                            {/* Поле пароля */}
                            <Box>
                                <Text fontSize="md" fontWeight="bold" color="gray.800" mb={3}>
                                    {t.login.form.passwordLabel}
                                </Text>
                                <Tooltip
                                    content={t.login.form.passwordDisabledHint}
                                    disabled={!isPasswordDisabled}
                                >
                                    <Box position="relative" w="full">
                                        <Input
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder={t.login.form.passwordPlaceholder}
                                            {...register('password')}
                                            disabled={isPasswordDisabled}
                                            size="lg"
                                            h="48px"
                                            pr="48px"
                                            borderRadius="0"
                                            _disabled={{
                                                opacity: 0.6,
                                                cursor: 'not-allowed',
                                                bg: 'gray.100',
                                            }}
                                            colorPalette={errors.password ? 'red' : 'gray'}
                                        />
                                        <Box
                                            position="absolute"
                                            right="16px"
                                            top="50%"
                                            transform="translateY(-50%)"
                                            cursor="pointer"
                                            onClick={() =>
                                                !isPasswordDisabled && setShowPassword(!showPassword)
                                            }
                                            color={isPasswordDisabled ? 'gray.400' : 'gray.600'}
                                            _hover={{
                                                color: isPasswordDisabled ? 'gray.400' : 'gray.800',
                                            }}
                                            transition="color 0.2s"
                                        >
                                            <Icon as={showPassword ? FiEyeOff : FiEye} boxSize={5} />
                                        </Box>
                                    </Box>
                                </Tooltip>
                                {errors.password && (
                                    <Text color="red.500" fontSize="sm" mt={1}>
                                        {errors.password.message}
                                    </Text>
                                )}
                            </Box>

                            {/* Чекбокс "Запомнить вход" */}
                            <HStack justify="space-between" align="center">
                                <RememberMeCheckbox
                                    checked={rememberMeController.field.value}
                                    onCheckedChange={rememberMeController.field.onChange}
                                    label={t.login.form.rememberMe}
                                    tooltipText={t.login.form.rememberMeTooltip}
                                    name={rememberMeController.field.name}
                                />
                            </HStack>

                            {/* Кнопка входа */}
                            <Button
                                type="submit"
                                buttonType="success"
                                size="lg"
                                disabled={!isValid || isLoading || isFetchingCompanies}
                                isLoading={isLoading}
                                loadingText={t.login.form.submitButtonLoading}
                            >
                                {t.login.form.submitButton}
                            </Button>

                            {/* Ссылка восстановления пароля */}
                            <HStack justify="center">
                                <Link
                                    color="blue.500"
                                    fontSize="md"
                                    _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                                    onClick={handleForgotPasswordClick}
                                >
                                    {t.login.form.forgotPassword}
                                </Link>
                            </HStack>

                            {/* Ссылка регистрации */}
                            <HStack justify="center" gap={1}>
                                <Text fontSize="md" color="gray.700">
                                    {t.login.form.noAccountText}
                                </Text>
                                <Link
                                    color="blue.500"
                                    fontSize="md"
                                    _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                                    onClick={() => navigate('/registration')}
                                >
                                    {t.login.form.registerLink}
                                </Link>
                            </HStack>
                        </VStack>
                    </form>
                </VStack>
            </Box>
        </Box>
    );
};

export default LoginPage;
